import {
  filter,
  fromDomEvent,
  fromPromise,
  fromValue,
  map,
  mergeMap,
  pipe,
  Source,
  take,
  tap,
} from 'wonka';
import { AdvertPayload } from './@types/ad-payload';
import { buildCreativeValues$ } from './creative';
import { debugPluginMessage, DEBUG_PLUGIN_EVENTS } from './debug';
import { getEnvironment, getOptions } from './environment';
import {
  addGlobalCallback,
  dropTrackers,
  makeTagUrl,
  removeGlobalCallback,
} from './io';
import { debugLog, log } from './logger';
import {
  getContainerDimension,
  getPageSizeDetails,
  resizeAdvert,
} from './size';
import { Tag } from './tag';
import { attachCeltraClickTrackers } from './third-party/celtra';
import { getMarkupTemplates, loadScript } from './utils/dom';
import { isFunction, isString, processMacros } from './utils/util';
import { applyRules$, setNoCenterForMPU } from './rules';
import { positionAdvert } from './position';

/**
 * All the options we pass to tag service to get the advertising payload
 */
export const getAdvertOptions = (tag: Tag) => {
  const environment = getEnvironment();
  const options = getOptions();
  const { constrainedMaxSize } = getPageSizeDetails(tag);
  const { pageOptions, config, auctionDetails } = tag;

  return {
    // Dimensions
    mw: constrainedMaxSize.width,
    mh: constrainedMaxSize.height,
    // Environment
    uif: Number(environment.isUnfriendly),
    sdk: environment.safeFrame ? 'SafeFrame' : 'MobileWeb',
    url: environment.url,
    amp: Number(environment.isAmp),
    mraid: Number(environment.mraid),
    // GDPR
    gdpr: pageOptions.gdpr || '',
    gdpr_consent: pageOptions.gdpr_consent || '',
    coppa: pageOptions.coppa || 0,
    // Third party clashes
    ens: Number(environment.isEnsighten),
    apn: Number(environment.isAppnexusAPN),
    ceo: Number(environment.createElementOverridden),
    // Internal Values
    iid: config.iid,
    idh: config.idh,
    aud: auctionDetails,
    tz: new Date().getTimezoneOffset(),
    test: pageOptions.test,
    debug: options.debug,
  };
};

export const getAdvertPayload$ = (
  tag: Tag,
  adOptions: ReturnType<typeof getAdvertOptions>,
): Source<Tag> =>
  pipe(
    fromPromise<AdvertPayload>(
      new Promise((resolve) => {
        const { endpoint } = getOptions();
        const tagCallback = addGlobalCallback<AdvertPayload>(window, resolve);

        const tagUrl = makeTagUrl({
          tagId: tag.tagId,
          params: adOptions,
          endpoint,
          tagCallback,
        });

        loadScript(
          tagUrl,
          // On Success
          (duration) => {
            log({
              tagId: tag.tagId,
              impressionId: adOptions.iid || '',
              reason: 'tag-success',
              tagd: duration,
            });
          },
          // On Error
          (duration) => {
            removeGlobalCallback(window, tagCallback);
            log({
              tagId: tag.tagId,
              impressionId: adOptions.iid || '',
              reason: 'tag-error',
              tagd: duration,
            });
          },
        );
      }),
    ),
    map((adPayload) => {
      const { passback } = getOptions();
      tag.payload = adPayload;

      // Log debug output
      debugLog(tag.payload.debug);

      if (tag.payload.err) {
        if (isString(passback)) {
          loadScript(processMacros({ tagId: tag.tagId }, passback));
        }
        if (isFunction(passback)) {
          passback(tag.target, tag.tagId);
        }

        tag.destroy();
      }

      return tag;
    }),
  );

const loadAdTrigger$ = (tag: Tag) =>
  pipe(
    fromValue(tag),
    mergeMap((tag) => {
      const { constraints = {} } = tag.pageOptions;
      const { isUnfriendly } = getEnvironment();
      if (isUnfriendly) {
        return fromValue(tag);
      }

      if (
        constraints.lean &&
        tag.container?.ownerDocument.readyState !== 'complete'
      ) {
        return pipe(
          fromDomEvent(
            tag.container!.ownerDocument.defaultView as unknown as HTMLElement,
            'load',
          ),
          map(() => tag),
        );
      }
      return fromValue(tag);
    }),
    take(1),
  );

/**
 * This is where the advert/markup actually gets rendered
 * Handle the html, children, click trackers and triggers
 */
const renderAdvertToPage = ({
  tag,
  markupTemplates,
}: {
  tag: Tag;
  markupTemplates: ReturnType<typeof getMarkupTemplates>;
}): Tag => {
  // Change container from original display "none" to "block" to make visible
  tag.container!.style.display = 'block';

  // Add all HTML to the page
  tag.creativeContainer!.innerHTML = markupTemplates.html.join('\n');

  // Attach all click trackers
  attachCeltraClickTrackers(tag, markupTemplates.clickTrackers);

  // Append all children to the tag container
  markupTemplates.children.forEach((element) => {
    tag.creativeContainer!.appendChild(element);
  });

  // Call any trigger functions we need to execute
  markupTemplates.triggers.forEach((trigger) => trigger());

  // Ensure we return the root tag back into the stream
  return tag;
};

export const loadAdvert$ = (tag: Tag) =>
  pipe(
    fromValue(getAdvertOptions(tag)),
    mergeMap((adOptions) =>
      pipe(
        getAdvertPayload$(tag, adOptions),
        // communicate with the debug plugin about advert status
        tap((tag) =>
          tag.destroyed
            ? debugPluginMessage(DEBUG_PLUGIN_EVENTS.fail, {
                _id: tag._id,
                err: tag.payload?.err,
              })
            : debugPluginMessage(DEBUG_PLUGIN_EVENTS.load, {
                _id: tag._id,
                campaignId: tag.payload?.mobkoi.campaignId,
                clazz: tag.payload?.creative.clazz,
              }),
        ),
        // remove tag from flow if we errored fetching the ad
        // doesn't make it to next stage
        filter((tag) => !tag.destroyed),
      ),
    ),
    mergeMap(applyRules$),
    mergeMap(buildCreativeValues$),
    mergeMap((builtCreativeValues) =>
      pipe(
        // Use this as a 'race' condition, we need to check whether we're good
        // to load, or wait for an event to trigger our load
        loadAdTrigger$(builtCreativeValues.tag),
        // ensure what we're returning out is the builtCreativeValues for the
        // next stage
        map(() => builtCreativeValues),
      ),
    ),
    map(renderAdvertToPage),
    map(positionAdvert),
    tap(resizeAdvert),
    tap(dropTrackers),
    tap((tag) => {
      log({
        reason: 'tmpl-apply',
        impressionId: tag.payload!.impressionId,
        tagId: tag.tagId,
        detail: getContainerDimension(tag),
      });

      // if tag is test, log it for network overview
      if (!!tag.pageOptions.test) {
        log({
          reason: 'tag-is-test',
          impressionId: tag.payload!.impressionId,
          campaignId: tag.payload!.mobkoi.campaignId,
          tagId: tag.tagId,
        });
      }
    }),
  );
